export type MedicineForm =
  | "tablet"
  | "capsule"
  | "pill"
  | "liquid"
  | "injection"
  | "patch"
  | "inhaler"
  | "spray"
  | "cream-ointment"
  | "drops"
  | "suppository"
  | "other";

export type Side = "left" | "right";
export type DayPattern = "daily" | "selected" | "alternate";
export type ScheduleMode = "fixed" | "interval" | "as-needed";
/**
 * "cycle" is a repeating pattern of days — take it on days 1–14 of every 21, six times over.
 * It sits here rather than beside the weekday patterns because that is where a plan states it:
 * how long the course runs is the question a cycle answers. See NOTES/decisions.md.
 */
export type CourseMode = "ongoing" | "duration" | "end-date" | "cycle";
export type DoseMode = "simple" | "complex";
export type CorrectionField = "amount" | "unit" | "dose-composition" | "target" | "actual-time" | "medicine" | "injection-region" | "side" | "note" | "other";

export type DoseComponent = {
  id: string;
  strength: string;
  defaultCount: number;
};

export type DoseStage = {
  id: string;
  label: string;
  startDate: string;
  /**
   * Absent means "runs until the prescription changes". A fixed combination — several
   * strengths added up to one dose, taken the same way every time — has no end date, and
   * forcing it to invent one made the dose silently collapse to its default counts the day
   * that invented date passed. See NOTES/bugs.md.
   */
  endDate?: string;
  targetAmount: string;
  componentCounts: { componentId: string; count: number }[];
};

export type ComponentSnapshot = {
  strength: string;
  strengthUnit: string;
  count: number;
};

/**
 * A daily maximum the user typed in, and what they were counting when they typed it.
 *
 * The basis is stored rather than inferred because the two readings differ whenever one dose
 * is more than one unit: "4 a day" means four tablets on a units basis and eight tablets on a
 * doses basis, and only the person who entered it knows which they meant. TED never converts
 * between them.
 */
export type DailyMaximum = {
  value: number;
  basis: "doses" | "units";
  /**
   * True when TED inferred the basis instead of being told it. Only the v8 → v9 migration
   * sets it: values entered before v0.33 carry no basis, and "units" is what every version
   * since v5 has compared them against. Anything saved from the editor states its basis, so
   * saving drops the flag.
   */
  basisAssumed?: true;
};

/**
 * How many units the user was given, so TED can say when they are running low.
 *
 * Ed's dad's suggestion, 4 September 2026. Every number here was typed by the user: TED counts
 * down from a quantity they entered, using doses they logged. It knows nothing about pack
 * sizes, dispensing intervals or what any medicine is, and it proposes nothing.
 *
 * The count is only ever as good as the logging — a dose taken and not logged leaves TED
 * believing there is more in the packet than there is. That is why the running count is shown
 * on the medicine's own screen rather than hidden: a number nobody can see is a number nobody
 * can correct. The warning says what it counted and from when, so it can be checked against
 * the packet, which stays the source of truth.
 */
export type MedicineSupply = {
  /** Units dispensed, as the user counted them — tablets, mL, sprays, in the medicine's unit. */
  quantity: number;
  /** The day that quantity was true. Doses logged on or after it count against it. */
  from: string;
  /** Say something once this many days of supply remain. */
  warnDays: number;
  /** When it was entered, so the warning can show its working. */
  recordedAt: string;
};

export type CorrectionChange = {
  field: CorrectionField;
  label: string;
  before: string;
  after: string;
};

export type Medicine = {
  id: string;
  name: string;
  form: MedicineForm;
  customForm?: string;
  defaultAmount: string;
  unit: string;
  doseMode: DoseMode;
  strength?: string;
  strengthUnit?: string;
  components: DoseComponent[];
  doseStages: DoseStage[];
  scheduleMode: ScheduleMode;
  scheduleTimes: string[];
  intervalHours?: number;
  intervalAnchorDate?: string;
  intervalAnchorTime?: string;
  asNeededIntervalHours?: number;
  dayPattern: DayPattern;
  weekdays: number[];
  alternateDayAnchor?: string;
  maxPerDay?: DailyMaximum;
  courseMode: CourseMode;
  courseStart?: string;
  courseDurationValue?: number;
  courseDurationUnit?: "days" | "weeks";
  courseEnd?: string;
  /**
   * A repeating cycle, used only when `courseMode` is "cycle". `courseStart` is day 1 of cycle 1.
   *
   * `cycleDays` are 1-based days within the cycle, so a 21-day cycle taken for its first
   * fortnight is `cycleLengthDays: 21, cycleDays: [1..14]`. It is a list rather than a range
   * because a regimen that skips — day 1 and day 8 of 21 — is common and a range cannot say it.
   *
   * TED counts days from a date the user gave it. It does not know, or propose, any regimen.
   */
  cycleLengthDays?: number;
  cycleDays?: number[];
  cycleCount?: number;
  /**
   * What the user takes it for. Several may apply, so this is a list — see the v10 → v11
   * migration for how the single `purpose` string became one.
   *
   * Entries are either a `PURPOSE_OPTIONS` label, `PURPOSE_NOT_SURE`, or one short piece of
   * text the user typed. TED never fills this in, and never infers one from a name.
   */
  purposes?: string[];
  /**
   * Absent on every medicine that does not count its supply, which is the normal case. Only
   * offered where the arithmetic can be honest — see `supplyEligible`.
   */
  supply?: MedicineSupply;
  linkedMedicineId?: string;
  requiresInjectionSite: boolean;
  /** Set when the user stops taking a medicine. It leaves Today but keeps its dose history readable. */
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type DoseEvent = {
  id: string;
  medicineId: string;
  actorId: string;
  scheduleKey?: string;
  scheduledFor?: string;
  actualTakenAt: string;
  amount: string;
  unit: string;
  doseStageId?: string;
  targetAmount?: string;
  targetUnit?: string;
  componentCounts?: { componentId: string; count: number }[];
  componentBreakdown?: ComponentSnapshot[];
  note?: string;
  injectionSite?: { region: string; side: Side };
  correctsEventId?: string;
  correctionFields?: CorrectionField[];
  correctionChanges?: CorrectionChange[];
  correctionOther?: string;
  recordedAt: string;
};

export type DoseVoid = {
  id: string;
  eventId: string;
  actorId: string;
  recordedAt: string;
};

export type MedicineCheck = {
  id: string;
  medicineId: string;
  actorId: string;
  checkedAt: string;
  medicineUpdatedAt: string;
};

export type TedStore = {
  schemaVersion: 12;
  profile: { id: string; displayName: string };
  medicines: Medicine[];
  doseEvents: DoseEvent[];
  doseVoids: DoseVoid[];
  medicineChecks: MedicineCheck[];
  onboardingCompletedAt?: string;
  lastBackupAt?: string;
  lastSeenAppVersion?: string;
  createdAt: string;
  updatedAt: string;
};

/** Where a card sits on Today: still to do, already done, or a standing medicine. */
export type TodayGroup = "outstanding" | "taken" | "everyday" | "resting";

export type ScheduleItem = {
  key: string;
  /**
   * `off-plan` is a dose recorded today for a medicine today's schedule does not include.
   * `rest-day` is a cycle medicine that is deliberately not taken today — nothing to do, shown
   * so the reader can see TED has not forgotten it.
   */
  kind: ScheduleMode | "off-plan" | "rest-day";
  group: TodayGroup;
  medicine: Medicine;
  time?: string;
  scheduledFor?: string;
  event?: DoseEvent;
  eventsToday: DoseEvent[];
  /** Doses recorded today for this medicine that belong to no scheduled occurrence today. */
  unlinkedEventsToday: DoseEvent[];
  /** Set on the first untaken occurrence, so an unlinked dose is reported once per medicine. */
  reportsUnlinked: boolean;
  /** Why a dose sits outside today's plan, stated as fact and never as fault. */
  offPlanNote?: string;
  /**
   * "rest-day" is deliberately its own state rather than borrowing "upcoming": the Today
   * counters read `upcoming` with no scheduled time as "Due later", so a rest day would have
   * been counted as something to do.
   */
  state: "taken" | "due" | "upcoming" | "as-needed" | "rest-day";
};

export const STORE_KEY = "ted_v0_store";
export const CURRENT_APP_VERSION = "TED v0.66";

/**
 * What changed, in the words a tester would use.
 *
 * Written for the person holding the phone, not for the commit log: what they will see or can
 * now do, and — where a release fixed something that had gone wrong for them — that it is
 * fixed. Newest first.
 *
 * Ed set the bar on 3 September 2026: substantive usable updates only. Anything cosmetic, or
 * anything behind the glass, they do not need to be told about; anything that changes how the
 * app is used, or adds something they can do, belongs here. So a release may legitimately have
 * no entry at all — that is the rule working, not a marker someone forgot to move, and nothing
 * should assert that the newest release is listed.
 *
 * Nothing in here may describe a dose as safe, or say anything about medicine. It is a list of
 * changes to an app.
 */
export const RELEASE_NOTES: { version: string; notes: string[] }[] = [
  { version: "TED v0.66", notes: [
    "A repeating schedule can be set in days as well as hours \u2014 \u201Cevery 3 days\u201D for a patch, rather than working out that it means 72 hours.",
    "The left/right question now uses the right words for what you are taking. A patch asks where you put it, drops ask which side, and only an injection calls it an injection.",
  ] },
  { version: "TED v0.64", notes: [
    "Medicines can be sorted by time to refill, soonest first. Medicines that are not counting a prescription are kept in a group of their own rather than hidden.",
  ] },
  { version: "TED v0.63", notes: [
    "TED can remind you to renew a prescription. Open a medicine, tap \u201CRemind me to renew my prescription\u201D and tell it how many you were given \u2014 it counts down from the doses you log and says something on Today before you run out.",
    "The count is on the medicine\u2019s own screen, with its working shown, so you can correct it whenever it drifts. TED can only count doses logged in TED, so the packet is still the real answer.",
  ] },
  { version: "TED v0.62", notes: [
    "TED no longer says you take an as-needed medicine \u201Cevery day\u201D. An as-needed medicine has no set frequency, so TED does not state one \u2014 it only says which days you can take it on, and only if you limited that yourself.",
  ] },
  { version: "TED v0.61", notes: [
    "Tapping a medicine now shows what it is \u2014 the dose, what you take it for and the rules you entered \u2014 instead of opening the setup form. Editing has its own button on each medicine.",
  ] },
  { version: "TED v0.60", notes: [
    "TED now tells you what has changed whenever it updates, once per release.",
  ] },
  { version: "TED v0.59", notes: [
    "History has a month view. Tap any day to read what was logged on it.",
  ] },
  { version: "TED v0.58", notes: [
    "History has a week view, showing all seven days at once.",
    "You can now switch History between Day, Week and Month. TED remembers which you chose.",
  ] },
  { version: "TED v0.57", notes: [
    "History is grouped by day, so it is easier to find a particular one.",
  ] },
  { version: "TED v0.56", notes: [
    "Fixed a blank screen that could appear when opening the details of a dose for a medicine made up of several strengths.",
  ] },
  { version: "TED v0.53", notes: [
    "TED no longer lets a dose be recorded at a time that has not happened yet.",
  ] },
  { version: "TED v0.52", notes: [
    "\u201CWhat do you take it for?\u201D replaces the old single-choice question, with twenty options and as many as apply.",
  ] },
];

/** "TED v0.59" becomes 59, so releases can be compared without parsing anything clever. */
const versionOrder = (version?: string) => {
  const match = /(\d+)\.(\d+)/.exec(version ?? "");
  return match ? Number(match[1]) * 1000 + Number(match[2]) : -1;
};

/**
 * Everything that changed since the reader last opened TED.
 *
 * Not just the newest release: a tester who was away for three of them should see all three
 * rather than be told about one and left to wonder about the rest. Returns nothing at all when
 * they are already up to date — which is what keeps the screen to once per version — and also
 * when the releases they missed changed nothing they would notice. The caller has to handle an
 * empty list rather than assume the newest release is in it.
 */
export function releaseNotesSince(lastSeen?: string) {
  const seen = versionOrder(lastSeen);
  // No recorded version means a store from before TED kept one. Showing the whole history to
  // someone whose first sight of this screen it is would be noise, so only the newest is shown.
  if (seen < 0) return RELEASE_NOTES.slice(0, 1);
  return RELEASE_NOTES.filter((release) => versionOrder(release.version) > seen);
}
export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const FORM_LABELS: Record<MedicineForm, string> = {
  tablet: "Tablet",
  capsule: "Capsule",
  pill: "Pill",
  liquid: "Liquid",
  injection: "Injection",
  patch: "Patch",
  inhaler: "Inhaler",
  spray: "Spray",
  "cream-ointment": "Cream / ointment",
  drops: "Drops",
  suppository: "Suppository",
  other: "Other",
};

export const UNIT_OPTIONS: Record<MedicineForm, string[]> = {
  tablet: ["tablet"], capsule: ["capsule"], pill: ["pill"], liquid: ["mL"], injection: ["injection"],
  patch: ["patch"], inhaler: ["puff"], spray: ["spray"], "cream-ointment": ["application"],
  drops: ["drop"], suppository: ["suppository"], other: ["dose"],
};

export type MedicineFormIconName = "tablet" | "capsule" | "injection" | "liquid" | "spray" | "other";

/** Twelve stored forms map onto Stillroom's six icons. Anything unrecognised falls back to `other`. */
const FORM_ICONS: Record<MedicineForm, MedicineFormIconName> = {
  tablet: "tablet", pill: "tablet",
  capsule: "capsule",
  liquid: "liquid", drops: "liquid",
  injection: "injection",
  spray: "spray", inhaler: "spray",
  patch: "other", "cream-ointment": "other", suppository: "other", other: "other",
};

export function formIconName(form: string): MedicineFormIconName {
  return FORM_ICONS[form as MedicineForm] ?? "other";
}

/**
 * What a medicine is taken for, as the user would say it.
 *
 * Alphabetical and left that way. Ranking them would be TED deciding which reasons are
 * common, which is a judgement about medicine it has no business making — and the order a
 * reader scans is stable if it never changes.
 *
 * Ed's wording, and "Effect" is deliberately not used anywhere: a reader can take it for a
 * side effect. The short label is "Purpose".
 */
export const PURPOSE_OPTIONS = [
  "Allergy relief",
  "Blood pressure / heart",
  "Blood sugar / diabetes",
  "Blood thinning / clot prevention",
  "Breathing / asthma",
  "Cancer treatment",
  "Cholesterol",
  "Constipation",
  "Diarrhoea",
  "Hormones / thyroid",
  "Infection",
  "Inflammation / immune control",
  "Mental health / mood",
  "Nausea / sickness",
  "Nerve pain / neuropathy",
  "Pain relief",
  "Seizure prevention",
  "Sleep",
  "Stomach protection / acid control",
  "Vitamins / minerals",
];

/**
 * Stored exactly like a preset so that reading a medicine needs no special case. It is
 * mutually exclusive with everything else: "not sure" and "for my blood pressure" cannot
 * both be true, and a list that says both says nothing.
 */
export const PURPOSE_NOT_SURE = "Not sure";

/** True for anything the user typed themselves rather than picked. */
export const isCustomPurpose = (value: string) => value !== PURPOSE_NOT_SURE && !PURPOSE_OPTIONS.includes(value);

/**
 * Every purpose a medicine carries, read the same way everywhere.
 *
 * Blanks and repeats are dropped rather than rendered: the field has existed as free text
 * since v0.1 and a store can hold "  " or the same word twice, neither of which should reach
 * a screen. Never throws on a missing or malformed list — an old medicine simply has none.
 */
export const medicinePurposes = (medicine: Pick<Medicine, "purposes">): string[] =>
  Array.isArray(medicine.purposes) ? [...new Set(medicine.purposes.filter((value) => typeof value === "string").map((value) => value.trim()).filter(Boolean))] : [];

export const uid = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
export const todayKey = (date = new Date()) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
export const isoFromLocal = (date: string, time: string) => new Date(`${date}T${time}:00`).toISOString();
export const timeFromDate = (date: Date) => `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
export const formatClock = (value: string | Date) => new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" }).format(typeof value === "string" ? new Date(value) : value);
export const formatDay = (value: string | Date) => new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" }).format(typeof value === "string" ? new Date(value) : value);
export const formatLongDay = (value: Date) => new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(value);
/**
 * A date that may be a long way off, with the year shown only when it differs from the date
 * being counted from. "Fri, 1 Jan" is ambiguous when today is August and the course runs six
 * 21-day cycles; "Fri, 1 Jan 2027" is not. Kept separate from `formatDay` so the twenty-odd
 * places showing a date in the current week are not made noisier to fix a distant one.
 */
export const formatDistantDay = (value: string, from: string) =>
  new Date(`${value}T12:00:00`).getFullYear() === new Date(`${from}T12:00:00`).getFullYear()
    ? formatDay(new Date(`${value}T12:00:00`))
    : new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(new Date(`${value}T12:00:00`));

export const sentenceCase = (value: string) => value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const addDays = (date: string, days: number) => {
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + days);
  return todayKey(next);
};

const dayDifference = (later: string, earlier: string) => Math.round((new Date(`${later}T12:00:00`).getTime() - new Date(`${earlier}T12:00:00`).getTime()) / 86_400_000);

export function courseDurationEnd(start: string, value: number, unit: "days" | "weeks") {
  const days = value * (unit === "weeks" ? 7 : 1);
  return addDays(start, Math.max(0, days - 1));
}

/** The last day of the final cycle. Counting only — the user stated the length and the count. */
export function cycleCourseEnd(start: string, lengthDays: number, cycles: number) {
  return addDays(start, Math.max(0, lengthDays * cycles - 1));
}

export function medicineEndDate(medicine: Medicine) {
  if (medicine.courseMode === "end-date") return medicine.courseEnd;
  if (medicine.courseMode === "duration" && medicine.courseStart && medicine.courseDurationValue) {
    return courseDurationEnd(medicine.courseStart, medicine.courseDurationValue, medicine.courseDurationUnit ?? "days");
  }
  if (medicine.courseMode === "cycle" && medicine.courseStart && medicine.cycleLengthDays && medicine.cycleCount) {
    return cycleCourseEnd(medicine.courseStart, medicine.cycleLengthDays, medicine.cycleCount);
  }
  return undefined;
}

/**
 * Which cycle a date falls in, and which day of it. Both 1-based, because that is how a plan is
 * written and read: "day 1", never "day 0".
 *
 * Undefined before the course starts, after the last cycle ends, or when the medicine carries no
 * cycle — so a caller that has one can rely on it, and everything else is untouched.
 */
export function cyclePositionOn(medicine: Medicine, date = todayKey()) {
  if (medicine.courseMode !== "cycle" || !medicine.courseStart || !medicine.cycleLengthDays || medicine.cycleLengthDays < 1) return undefined;
  const elapsed = dayDifference(date, medicine.courseStart);
  if (elapsed < 0) return undefined;
  const cycle = Math.floor(elapsed / medicine.cycleLengthDays) + 1;
  if (medicine.cycleCount && cycle > medicine.cycleCount) return undefined;
  return { cycle, day: (elapsed % medicine.cycleLengthDays) + 1, of: medicine.cycleCount, length: medicine.cycleLengthDays };
}

/** A day inside the course that the cycle says is not a taking day. Never true off a cycle. */
export function isCycleRestDay(medicine: Medicine, date = todayKey()) {
  if (medicine.archivedAt) return false;
  const position = cyclePositionOn(medicine, date);
  return !!position && !(medicine.cycleDays ?? []).includes(position.day);
}

/** "Cycle 3 of 6 · day 9", or "Cycle 3 · day 9" where no number of cycles was entered. */
export function cyclePositionLabel(medicine: Medicine, date = todayKey()) {
  const position = cyclePositionOn(medicine, date);
  if (!position) return undefined;
  return `${position.of ? `Cycle ${position.cycle} of ${position.of}` : `Cycle ${position.cycle}`} · day ${position.day}`;
}

/**
 * Whether TED can count this medicine's supply honestly.
 *
 * Ed, 4 September 2026: simple scheduled medicines first. The two exclusions are not
 * squeamishness, they are cases where the arithmetic has no meaning:
 *
 * - **As needed.** There is no rate at which it is taken, so "about a week left" cannot be
 *   calculated from anything. TED would have to guess how much you will take next week, and
 *   guessing is the one thing it must not do.
 * - **A dose made of several strengths.** One dose is drawn from two or three separate boxes,
 *   each with its own count. A single remaining number would describe none of them.
 *
 * Both could be supported later — the as-needed case by counting down units instead of days,
 * the complex case by a count per strength. Neither is this change.
 */
export function supplyEligible(medicine: Medicine) {
  return medicine.doseMode === "simple" && medicine.scheduleMode !== "as-needed";
}

/**
 * How many units a day the medicine's own schedule accounts for.
 *
 * Doses per active day multiplied by the share of days it is taken on — the same rules
 * `isMedicineOnDate` applies, so the burn rate agrees with what Today shows. Returns nothing
 * where any part of it is missing or nonsensical rather than substituting a number, because a
 * wrong rate would produce a confident, wrong date to reorder by.
 */
export function unitsPerDay(medicine: Medicine) {
  const amount = Number(medicine.defaultAmount);
  if (!Number.isFinite(amount) || amount <= 0) return undefined;

  let dosesPerActiveDay: number;
  if (medicine.scheduleMode === "interval") {
    if (!medicine.intervalHours || medicine.intervalHours <= 0) return undefined;
    dosesPerActiveDay = 24 / medicine.intervalHours;
  } else {
    if (!medicine.scheduleTimes.length) return undefined;
    dosesPerActiveDay = medicine.scheduleTimes.length;
  }

  let activeShare: number;
  if (medicine.courseMode === "cycle") {
    if (!medicine.cycleLengthDays || !(medicine.cycleDays ?? []).length) return undefined;
    activeShare = (medicine.cycleDays as number[]).length / medicine.cycleLengthDays;
  } else if (medicine.scheduleMode === "interval") {
    // An interval medicine ignores the weekday pattern, exactly as `isMedicineOnDate` does.
    activeShare = 1;
  } else if (medicine.dayPattern === "alternate") {
    activeShare = 0.5;
  } else if (medicine.dayPattern === "selected") {
    if (!medicine.weekdays.length) return undefined;
    activeShare = medicine.weekdays.length / 7;
  } else {
    activeShare = 1;
  }

  const perDay = amount * dosesPerActiveDay * activeShare;
  return perDay > 0 ? perDay : undefined;
}

/**
 * How long is left, in words.
 *
 * Zero is the case worth having a rule for: "roughly 0 days" is both odd to read and quietly
 * alarming in the wrong way — it sounds like a deadline that has passed rather than a packet
 * that is nearly out. "Less than a day" says the same arithmetic without the false precision.
 */
export function daysLeftPhrase(daysLeft: number) {
  if (daysLeft <= 0) return "less than a day";
  return daysLeft === 1 ? "about a day" : `about ${daysLeft} days`;
}

export type SupplyState = {
  /** What the user entered, and the day they said it was true. */
  quantity: number;
  from: string;
  recordedAt: string;
  /** Units TED has counted since `from`, from logged doses only. */
  used: number;
  /** Never below zero — see `overrun` for the case that would have taken it there. */
  remaining: number;
  /** More has been logged than was entered, so the figure cannot be right. */
  overrun: boolean;
  perDay?: number;
  /** Whole days, rounded down. Absent when the schedule gives no usable rate. */
  daysLeft?: number;
  /** At or under the threshold the user set. */
  low: boolean;
};

/**
 * What is left, counted from doses that were actually logged.
 *
 * Logged, not scheduled: a scheduled dose is never taken until an explicit dose event exists,
 * and this follows that rule rather than assuming the plan was kept. The consequence is worth
 * being plain about — an unlogged dose leaves TED over-counting the packet, so the warning
 * arrives late. That is why nothing here is presented as authoritative and the packet stays the
 * source of truth.
 *
 * Corrections and deletions are handled by counting active events only, so a voided dose stops
 * being counted against the supply exactly as it stops being counted anywhere else.
 */
export function supplyState(medicine: Medicine, events: DoseEvent[], voids: DoseVoid[] = [], today = todayKey()): SupplyState | undefined {
  const supply = medicine.supply;
  if (!supply) return undefined;

  const used = getActiveEvents(events, voids)
    .filter((event) => event.medicineId === medicine.id && todayKey(new Date(event.actualTakenAt)) >= supply.from)
    .reduce((total, event) => {
      const amount = Number(event.amount);
      return Number.isFinite(amount) ? total + amount : total;
    }, 0);

  const perDay = unitsPerDay(medicine);
  const remaining = Math.max(0, supply.quantity - used);
  const daysLeft = perDay ? Math.floor(remaining / perDay) : undefined;

  return {
    quantity: supply.quantity, from: supply.from, recordedAt: supply.recordedAt,
    used, remaining, overrun: used > supply.quantity, perDay, daysLeft,
    // An overrun is low by definition: whatever is really in the packet, TED cannot say it is
    // a comfortable amount. A course that has already ended is not — see `renewalDue`.
    low: daysLeft !== undefined ? daysLeft <= supply.warnDays : used > supply.quantity,
  };
}

/**
 * The medicines worth mentioning on Today, in the order they run out.
 *
 * A medicine whose course has already ended is left out: there is nothing to reorder for a
 * course that has finished, and saying so would be TED nagging about a medicine the user has
 * deliberately stopped.
 */
export function renewalsDue(medicines: Medicine[], events: DoseEvent[], voids: DoseVoid[] = [], today = todayKey()) {
  return medicines
    .filter((medicine) => !medicine.archivedAt)
    .map((medicine) => ({ medicine, supply: supplyState(medicine, events, voids, today) }))
    .filter((entry): entry is { medicine: Medicine; supply: SupplyState } => {
      if (!entry.supply?.low) return false;
      const end = medicineEndDate(entry.medicine);
      return !(end && today > end);
    })
    .sort((a, b) => (a.supply.daysLeft ?? -1) - (b.supply.daysLeft ?? -1));
}

/**
 * An interval said the way the person setting it up would say it.
 *
 * A tester with a patch changed every three days could already record it — the interval is
 * stored in hours and 72 works correctly — but the field asks "every how many hours?" with "6"
 * as its example, and nobody thinks of their patch in hours. The storage is not the problem and
 * does not change: this is only about which way round the question is asked.
 *
 * Whole days only. 36 hours is a day and a half and stays in hours, because "every 1.5 days" is
 * a worse sentence than the one it replaces.
 */
export function intervalInDays(hours?: number) {
  return hours && hours >= 24 && hours % 24 === 0 ? hours / 24 : undefined;
}

/** "Every 3 days" where the interval divides into days, "Every 8 hours" where it does not. */
export function intervalPhrase(hours?: number) {
  if (!hours) return "";
  const days = intervalInDays(hours);
  if (days) return days === 1 ? "Every day" : `Every ${days} days`;
  return `Every ${hours} ${hours === 1 ? "hour" : "hours"}`;
}

/**
 * What to call the place a dose went, and which places to offer.
 *
 * A tester with a patch found the left/right question and liked it — "never seen that on a med
 * tracker" — but everything around it said *injection*. The toggle has never been gated by form,
 * which is why they found it at all; only the words were wrong.
 *
 * **Nothing stored changes.** `requiresInjectionSite`, the `injectionSite` on a dose event and
 * the `injection-region` correction key all keep their names: renaming them needs a migration
 * and buys nothing anybody can see. This is a lookup from form to wording, and no more.
 *
 * The areas are places on a body, not medication details — the same kind of list the injection
 * form has offered since v0.12. TED still proposes nothing about where a dose *should* go, and
 * the sheet always opens in full when a site is wanted, so the first option is never taken
 * silently.
 */
export type SiteWording = { toggle: string; toggleHelp: string; field: string; areaLabel: string; lastLabel: string; areas: string[] };

const SITE_WORDING: Partial<Record<MedicineForm, SiteWording>> = {
  injection: {
    toggle: "Record injection site", toggleHelp: "Ask for region and left/right when logging",
    field: "Injection site", areaLabel: "Region", lastLabel: "Last injection site",
    areas: ["Abdomen", "Thigh", "Upper arm", "Other"],
  },
  patch: {
    toggle: "Record where you put it", toggleHelp: "Ask for the area and left/right when logging",
    field: "Where did you put it?", areaLabel: "Area", lastLabel: "Last patch site",
    areas: ["Upper arm", "Chest", "Back", "Abdomen", "Hip", "Other"],
  },
  "cream-ointment": {
    toggle: "Record where you applied it", toggleHelp: "Ask for the area and left/right when logging",
    field: "Where did you apply it?", areaLabel: "Area", lastLabel: "Last applied to",
    areas: ["Hand", "Arm", "Leg", "Foot", "Torso", "Other"],
  },
  drops: {
    toggle: "Record which side", toggleHelp: "Ask for the area and left/right when logging",
    field: "Where did they go?", areaLabel: "Area", lastLabel: "Last given in",
    areas: ["Eye", "Ear", "Nose", "Other"],
  },
  spray: {
    toggle: "Record which side", toggleHelp: "Ask for the area and left/right when logging",
    field: "Where did it go?", areaLabel: "Area", lastLabel: "Last given in",
    areas: ["Nostril", "Other"],
  },
};

/**
 * Anything without its own wording gets a plain version rather than injection's.
 *
 * Plain body areas, deliberately not "left side / right side" — that was the first draft and it
 * duplicates the left/right question sitting directly beneath it, which would have read as
 * asking the same thing twice.
 */
const GENERIC_SITE: SiteWording = {
  toggle: "Record where it goes", toggleHelp: "Ask for the area and left/right when logging",
  field: "Where did it go?", areaLabel: "Area", lastLabel: "Last recorded site",
  areas: ["Arm", "Leg", "Torso", "Other"],
};

export function siteWording(form: MedicineForm): SiteWording {
  return SITE_WORDING[form] ?? GENERIC_SITE;
}

export function isMedicineOnDate(medicine: Medicine, date: string) {
  if (medicine.archivedAt) return false;
  if (medicine.courseStart && date < medicine.courseStart) return false;
  const end = medicineEndDate(medicine);
  if (end && date > end) return false;
  if (medicine.doseMode === "complex" && medicine.doseStages.length && !doseStageForDate(medicine, date)) return false;
  // A cycle answers "which days", so it takes over from the weekday patterns rather than
  // stacking with them: two rules that can quietly disagree, behind the screen that answers
  // "what do I take today", is the trap this avoids. Decided 29 Aug 2026.
  if (medicine.courseMode === "cycle") {
    const position = cyclePositionOn(medicine, date);
    return !!position && (medicine.cycleDays ?? []).includes(position.day);
  }
  if (medicine.scheduleMode === "interval") return true;
  if (medicine.dayPattern === "selected") return medicine.weekdays.includes(new Date(`${date}T12:00:00`).getDay());
  if (medicine.dayPattern === "alternate") {
    const anchor = medicine.alternateDayAnchor ?? medicine.courseStart;
    return !!anchor && dayDifference(date, anchor) >= 0 && dayDifference(date, anchor) % 2 === 0;
  }
  return true;
}

/**
 * The record that a dose entry has been withdrawn.
 *
 * Deleting an entry and undoing a log are the same act on the ledger: the event stays,
 * a void is appended beside it, and `getActiveEvents` stops counting it. Neither ever
 * removes a `DoseEvent`, which is why both go through here rather than each building the
 * object themselves — an undo that erased would be the one thing the ledger must not do.
 */
export function makeDoseVoid(store: TedStore, eventId: string, now = new Date()): DoseVoid {
  return { id: uid(), eventId, actorId: store.profile.id, recordedAt: now.toISOString() };
}

export function getActiveEvents(events: DoseEvent[], voids: DoseVoid[] = []) {
  const corrected = new Set(events.filter((event) => event.correctsEventId).map((event) => event.correctsEventId as string));
  const voided = new Set(voids.map((entry) => entry.eventId));
  return events.filter((event) => !corrected.has(event.id) && !voided.has(event.id));
}

export function totalDoseAmount(events: DoseEvent[]) {
  return events.reduce((total, event) => {
    const amount = Number(event.amount);
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);
}

export function projectedDoseAmountForDay(events: DoseEvent[], voids: DoseVoid[], medicineId: string, date: string, proposedAmount: string | number, replacingEventId?: string) {
  const amount = Number(proposedAmount);
  if (!Number.isFinite(amount)) return undefined;
  const earlier = getActiveEvents(events, voids).filter((event) => event.id !== replacingEventId && event.medicineId === medicineId && todayKey(new Date(event.actualTakenAt)) === date);
  return totalDoseAmount(earlier) + amount;
}

/** Doses already recorded that day, counting the one about to be saved. */
export function projectedDoseCountForDay(events: DoseEvent[], voids: DoseVoid[], medicineId: string, date: string, replacingEventId?: string) {
  return getActiveEvents(events, voids).filter((event) => event.id !== replacingEventId && event.medicineId === medicineId && todayKey(new Date(event.actualTakenAt)) === date).length + 1;
}

/**
 * Where the dose about to be saved leaves the day against the maximum the user entered,
 * measured on the basis they entered it on. Returns nothing when no maximum was entered.
 */
export function dailyMaximumCheck(medicine: Medicine, events: DoseEvent[], voids: DoseVoid[], date: string, proposedAmount: string | number, replacingEventId?: string) {
  const max = medicine.maxPerDay;
  if (!max) return undefined;
  if (max.basis === "doses") {
    const projected = projectedDoseCountForDay(events, voids, medicine.id, date, replacingEventId);
    return { basis: max.basis, projected, limit: max.value, exceeds: projected > max.value };
  }
  const projected = projectedDoseAmountForDay(events, voids, medicine.id, date, proposedAmount, replacingEventId);
  if (projected === undefined) return undefined;
  return { basis: max.basis, projected, limit: max.value, exceeds: projected > max.value };
}

/** "4 doses" or "4 tablets", as the user entered it. Never converted between the two. */
export function formatDailyMaximum(max: DailyMaximum, unit: string) {
  return max.basis === "doses" ? `${max.value} ${max.value === 1 ? "dose" : "doses"}` : formatDoseAmount(max.value, unit);
}

/**
 * Up to schema v4 the daily limit was `maxDosesPerDay` and counted logging events. From v5
 * it was `maxAmountPerDay` and was compared against the total amount recorded that day, in
 * the medicine's own unit. `migrateV4` carried the number across unchanged, so a limit
 * entered against a dose of more than one unit no longer meant what the user intended —
 * "4 doses" became "4 tablets".
 *
 * From v9 the basis is stored alongside the number, so nothing entered from v0.33 onwards is
 * ambiguous. Values carried up from older schemas arrive flagged `basisAssumed`, and where
 * one dose is more than one unit the assumption may not be what the user meant.
 *
 * TED must never convert these values, because only the user knows what they meant. It can
 * only surface them for the user to re-confirm against their own source.
 */
export function dailyLimitNeedsConfirming(medicine: Medicine) {
  return medicine.maxPerDay?.basisAssumed === true && Number(medicine.defaultAmount) > 1;
}

/**
 * How long past its time a scheduled dose has to be before Today says so.
 *
 * It is deliberately the mirror of the "due soon" window rather than a new number: two hours
 * ahead is "soon", two hours behind is "earlier". TED is not making a clinical judgement and
 * must not appear to — the card keeps showing the scheduled time so the reader judges, and
 * the wording is a plain statement of the clock, never a rebuke.
 */
/**
 * "20 minutes ago", "2 hours ago", "in 25 minutes" — the gap between two times, in words.
 *
 * Rounded to the unit a reader would use out loud rather than reported precisely: nobody
 * wants "1 hour and 47 minutes ago" on the screen that is meant to be read at a glance. The
 * exact clock time is always shown beside it, so nothing is lost to the rounding.
 */
export function formatGap(from: string | Date, now: Date) {
  const minutes = Math.round((now.getTime() - new Date(from).getTime()) / 60_000);
  const ago = minutes >= 0;
  const size = Math.abs(minutes);
  const say = size < 1 ? "just now"
    : size < 60 ? `${size} minute${size === 1 ? "" : "s"}`
    : size < 60 * 36 ? `${Math.round(size / 60)} hour${Math.round(size / 60) === 1 ? "" : "s"}`
    : `${Math.round(size / 1440)} day${Math.round(size / 1440) === 1 ? "" : "s"}`;
  if (say === "just now") return say;
  return ago ? `${say} ago` : `in ${say}`;
}

export const BEHIND_AFTER_MINUTES = 120;

/** Minutes past a scheduled dose's time, or 0 for anything not late. */
export function minutesBehind(item: ScheduleItem, now: Date) {
  if (item.state !== "due" || !item.scheduledFor) return 0;
  return Math.max(0, Math.round((now.getTime() - new Date(item.scheduledFor).getTime()) / 60_000));
}

/** Late enough to be worth marking. */
export function isBehind(item: ScheduleItem, now: Date) {
  return minutesBehind(item, now) > BEHIND_AFTER_MINUTES;
}

/**
 * The second line of Today's glance box: the dose the reader has slipped furthest behind on,
 * or failing that the next one due, or nothing left to do.
 *
 * The order matters and is the whole point of the box. The card list already states every
 * dose's time, so "next" is largely a restatement of the first card — Ed said as much. What
 * the list cannot show at a glance is which outstanding dose is the oldest, because it is
 * somewhere down a scroll in a card that looks exactly like one due shortly.
 */
export type TodayGlance =
  | { kind: "behind"; item: ScheduleItem; minutes: number }
  | { kind: "next"; item: ScheduleItem }
  | { kind: "clear" };

export function todayGlance(items: ScheduleItem[], now: Date): TodayGlance {
  const scheduled = items.filter((item) => item.kind !== "as-needed" && item.kind !== "rest-day");
  const behind = scheduled
    .filter((item) => isBehind(item, now))
    .sort((a, b) => (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
  if (behind[0]) return { kind: "behind", item: behind[0], minutes: minutesBehind(behind[0], now) };
  const ahead = scheduled
    .filter((item) => (item.state === "due" || item.state === "upcoming") && !!item.scheduledFor)
    .sort((a, b) => (a.scheduledFor as string).localeCompare(b.scheduledFor as string));
  return ahead[0] ? { kind: "next", item: ahead[0] } : { kind: "clear" };
}

/** The most recently recorded dose still standing, whenever it was taken. */
export function lastLoggedDose(events: DoseEvent[], voids: DoseVoid[] = []) {
  return [...getActiveEvents(events, voids)]
    .sort((a, b) => b.actualTakenAt.localeCompare(a.actualTakenAt))[0];
}

/** Plain fact about why a medicine is not on today's plan. Never phrased as a transgression. */
function offPlanReason(medicine: Medicine, date: string) {
  if (medicine.archivedAt) return "No longer taking this";
  const end = medicineEndDate(medicine);
  if (end && date > end) return `Course ended ${formatDay(new Date(`${end}T12:00:00`))}`;
  if (medicine.courseStart && date < medicine.courseStart) return `Course starts ${formatDay(new Date(`${medicine.courseStart}T12:00:00`))}`;
  return "Not scheduled today";
}

function intervalDatesForDay(medicine: Medicine, date: string) {
  if (!medicine.intervalHours || !medicine.intervalAnchorDate || !medicine.intervalAnchorTime) return [];
  const step = medicine.intervalHours * 3_600_000;
  const anchor = new Date(`${medicine.intervalAnchorDate}T${medicine.intervalAnchorTime}:00`).getTime();
  const dayStart = new Date(`${date}T00:00:00`).getTime();
  const dayEnd = new Date(`${date}T23:59:59.999`).getTime();
  if (!Number.isFinite(anchor) || !Number.isFinite(step) || step <= 0 || anchor > dayEnd) return [];
  const firstIndex = Math.max(0, Math.ceil((dayStart - anchor) / step));
  const dates: Date[] = [];
  for (let time = anchor + firstIndex * step; time <= dayEnd && dates.length < 48; time += step) dates.push(new Date(time));
  return dates;
}

export function buildTodayItems(store: TedStore, now: Date): ScheduleItem[] {
  const date = todayKey(now);
  const activeEvents = getActiveEvents(store.doseEvents, store.doseVoids);
  const todayEvents = activeEvents.filter((event) => todayKey(new Date(event.actualTakenAt)) === date);
  const onPlan = store.medicines.filter((medicine) => isMedicineOnDate(medicine, date));
  const onPlanIds = new Set(onPlan.map((medicine) => medicine.id));

  // A dose taken today for a medicine today's schedule does not include has nowhere to
  // appear, because Today is otherwise built entirely from the schedule. Today exists to
  // answer "what have I taken", so each such dose gets a card of its own.
  const offPlan: ScheduleItem[] = todayEvents
    .filter((event) => !onPlanIds.has(event.medicineId))
    .flatMap((event) => {
      const medicine = store.medicines.find((item) => item.id === event.medicineId);
      if (!medicine) return [];
      return [{
        key: `${date}|off-plan|${event.id}`, kind: "off-plan", group: "taken", medicine,
        event, eventsToday: [event], unlinkedEventsToday: [], reportsUnlinked: false,
        offPlanNote: offPlanReason(medicine, date), state: "taken",
      } as ScheduleItem];
    });

  // A cycle's rest days. Only a medicine on a cycle can produce one, so this is invisible to
  // everyone else. A dose recorded on a rest day already gets an off-plan card above, and two
  // cards for one medicine would be worse than none.
  const eventMedicineIds = new Set(todayEvents.map((event) => event.medicineId));
  const resting: ScheduleItem[] = store.medicines
    .filter((medicine) => !onPlanIds.has(medicine.id) && !eventMedicineIds.has(medicine.id) && isCycleRestDay(medicine, date))
    .map((medicine) => ({
      key: `${date}|${medicine.id}|rest-day`, kind: "rest-day", group: "resting", medicine,
      eventsToday: [], unlinkedEventsToday: [], reportsUnlinked: false,
      offPlanNote: cyclePositionLabel(medicine, date), state: "rest-day",
    }));

  return onPlan
    .flatMap((medicine): ScheduleItem[] => {
      const medicineEvents = todayEvents.filter((event) => event.medicineId === medicine.id);
      if (medicine.scheduleMode === "as-needed") {
        const latestMedicineEvent = activeEvents
          .filter((event) => event.medicineId === medicine.id)
          .sort((a, b) => b.actualTakenAt.localeCompare(a.actualTakenAt))[0];
        return [{
          key: `${date}|${medicine.id}|as-needed`, kind: "as-needed", group: "everyday", medicine,
          event: latestMedicineEvent,
          eventsToday: medicineEvents, unlinkedEventsToday: [], reportsUnlinked: false, state: "as-needed",
        }];
      }
      const scheduledDates = medicine.scheduleMode === "interval"
        ? intervalDatesForDay(medicine, date)
        : medicine.scheduleTimes.map((time) => new Date(isoFromLocal(date, time)));
      const rows = scheduledDates.map((scheduledDate) => {
        const time = timeFromDate(scheduledDate);
        const scheduledFor = scheduledDate.toISOString();
        return { scheduledDate, time, scheduledFor, key: medicine.scheduleMode === "interval" ? `${date}|${medicine.id}|interval|${scheduledFor}` : `${date}|${medicine.id}|${time}` };
      });
      // A dose recorded against no occurrence of today's schedule stays invisible on Today
      // unless it is reported. TED does not guess which occurrence it belonged to.
      const todaysKeys = new Set(rows.map((row) => row.key));
      const unlinkedEventsToday = medicineEvents.filter((event) => !event.scheduleKey || !todaysKeys.has(event.scheduleKey));
      let reported = false;
      return rows.map(({ scheduledDate, time, scheduledFor, key }) => {
        const event = activeEvents.find((dose) => dose.scheduleKey === key);
        const state = event ? "taken" : scheduledDate <= now ? "due" : "upcoming";
        const reportsUnlinked = !reported && state !== "taken" && unlinkedEventsToday.length > 0;
        if (reportsUnlinked) reported = true;
        return { key, kind: medicine.scheduleMode, group: state === "taken" ? "taken" : "outstanding", medicine, time, scheduledFor, event, eventsToday: medicineEvents, unlinkedEventsToday, reportsUnlinked, state } as ScheduleItem;
      });
    })
    .concat(offPlan)
    .concat(resting)
    .sort((a, b) => {
      // Still to take, then what has been taken, then the standing medicines. Anything
      // outstanding must never sit below something already dealt with.
      const rank = (item: ScheduleItem) => item.group === "outstanding" ? 0 : item.group === "taken" ? 1 : 2;
      const byGroup = rank(a) - rank(b);
      if (byGroup) return byGroup;
      if (a.group === "taken") {
        // Chronological, so the day reads in the order it happened.
        return (a.event?.actualTakenAt ?? a.scheduledFor ?? "").localeCompare(b.event?.actualTakenAt ?? b.scheduledFor ?? "");
      }
      if (a.kind === "as-needed") {
        const aTaken = a.eventsToday.length > 0, bTaken = b.eventsToday.length > 0;
        if (aTaken !== bTaken) return aTaken ? -1 : 1;
        const latestFirst = (b.event?.actualTakenAt ?? "").localeCompare(a.event?.actualTakenAt ?? "");
        if (aTaken && bTaken && latestFirst) return latestFirst;
        return a.medicine.name.localeCompare(b.medicine.name);
      }
      return (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? "");
    });
}

export function formLabel(medicine: Medicine) {
  return medicine.form === "other" && medicine.customForm ? medicine.customForm : FORM_LABELS[medicine.form];
}

export function defaultUnitForForm(form: MedicineForm) {
  return UNIT_OPTIONS[form][0];
}

const UNIT_PLURALS: Record<string, string> = { tablet: "tablets", capsule: "capsules", pill: "pills", injection: "injections", patch: "patches", puff: "puffs", spray: "sprays", application: "applications", drop: "drops", suppository: "suppositories", dose: "doses" };

export function pluralUnit(unit: string) {
  return UNIT_PLURALS[unit.toLowerCase()] ?? unit;
}

export function formatDoseAmount(amount: string | number, unit: string) {
  return `${amount} ${Number(amount) === 1 ? unit : pluralUnit(unit)}`;
}

/**
 * For an as-needed medicine, `dayPattern` says which days it *may* be logged on — see
 * `isMedicineOnDate`, where it gates whether the medicine appears on Today at all. It does not
 * say how often it is taken, because an as-needed medicine has no such number: that is what
 * "as needed" means.
 *
 * Reported as "Every day" it read as a frequency, and TED was telling Ed he takes morphine and
 * codeine daily. In a medication tracker that is not untidy wording, it is a false statement
 * about medication use — so the unrestricted case now says nothing at all, and a restriction is
 * reported as the restriction it is. Ed, 4 September 2026.
 */
export function asNeededDayLimit(medicine: Medicine) {
  if (medicine.dayPattern === "daily") return undefined;
  return medicine.dayPattern === "alternate" ? "Alternate days" : medicine.weekdays.map((weekday) => WEEKDAYS[weekday]).join(", ");
}

export function scheduleSummary(medicine: Medicine) {
  const day = medicine.dayPattern === "daily" ? "Every day" : medicine.dayPattern === "alternate" ? "Every other day" : medicine.weekdays.map((weekday) => WEEKDAYS[weekday]).join(", ");
  if (medicine.scheduleMode === "as-needed") {
    const limit = asNeededDayLimit(medicine);
    const details = [medicine.asNeededIntervalHours ? `your instruction: every ${medicine.asNeededIntervalHours} hours` : "", medicine.maxPerDay ? `stated maximum ${formatDailyMaximum(medicine.maxPerDay, medicine.unit)}/day` : ""].filter(Boolean).join(" · ");
    return `As needed${details ? ` · ${details}` : ""}${limit ? ` · ${limit.toLowerCase()} only` : ""}`;
  }
  if (medicine.scheduleMode === "interval") return `${intervalPhrase(medicine.intervalHours)} · from ${medicine.intervalAnchorDate} at ${medicine.intervalAnchorTime}`;
  return `${day} · ${medicine.scheduleTimes.map((time) => formatClock(isoFromLocal(todayKey(), time))).join(", ")}`;
}

/**
 * A medicine's current rules, written out as labelled rows.
 *
 * `scheduleSummary` packs the same facts into one dense line, which is right on a list card and
 * wrong on a screen whose whole job is to be read. This unpacks them: one row per thing, so a
 * reader who wants to know what days it is taken on does not have to parse a sentence with four
 * separators in it.
 *
 * Every value here is something the user typed or a date TED counted from one. Nothing is
 * inferred about the medicine, and nothing states or implies that a dose is due, permitted or
 * safe — an interval and a daily maximum are reported as *their* instruction, in the same words
 * `scheduleSummary` uses.
 *
 * Rows are omitted rather than filled with "None": a medicine with no course has nothing to say
 * about one, and an empty row is a thing to read before you can discard it.
 */
export function medicineFacts(medicine: Medicine, date = todayKey()): { label: string; value: string }[] {
  const facts: { label: string; value: string }[] = [];
  const day = (value: string) => formatDistantDay(value, date);

  if (medicine.scheduleMode === "as-needed") {
    facts.push({ label: "When", value: "As needed" });
    if (medicine.asNeededIntervalHours) {
      facts.push({ label: "Your stated interval", value: `Every ${medicine.asNeededIntervalHours} hours` });
    }
  } else if (medicine.scheduleMode === "interval") {
    facts.push({ label: "When", value: intervalPhrase(medicine.intervalHours) });
    if (medicine.intervalAnchorDate && medicine.intervalAnchorTime) {
      facts.push({ label: "Counted from", value: `${day(medicine.intervalAnchorDate)} at ${formatClock(isoFromLocal(medicine.intervalAnchorDate, medicine.intervalAnchorTime))}` });
    }
  } else if (medicine.scheduleTimes.length) {
    facts.push({ label: medicine.scheduleTimes.length === 1 ? "Scheduled time" : "Scheduled times", value: medicine.scheduleTimes.map((time) => formatClock(isoFromLocal(todayKey(), time))).join(", ") });
  }

  // A cycle answers "which days" on its own — see `isMedicineOnDate`. Saying "every day" as
  // well would be two rules disagreeing on the screen that exists to explain the rules.
  if (medicine.courseMode === "cycle") {
    if (medicine.cycleLengthDays && (medicine.cycleDays ?? []).length) {
      const days = [...(medicine.cycleDays ?? [])].sort((a, b) => a - b);
      facts.push({ label: "Which days", value: `${days.length === 1 ? "Day" : "Days"} ${days.join(", ")} of every ${medicine.cycleLengthDays}` });
    }
  } else if (medicine.scheduleMode === "as-needed") {
    // Only when the user restricted it. "Every day" here would be TED stating a frequency for
    // a medicine that has none — see `asNeededDayLimit`.
    const limit = asNeededDayLimit(medicine);
    if (limit) facts.push({ label: "Only on", value: limit });
  } else if (medicine.scheduleMode !== "interval") {
    const pattern = medicine.dayPattern === "daily" ? "Every day"
      : medicine.dayPattern === "alternate" ? "Every other day"
      : medicine.weekdays.map((weekday) => WEEKDAYS[weekday]).join(", ");
    if (pattern) facts.push({ label: "Which days", value: pattern });
  }

  if (medicine.maxPerDay) {
    facts.push({ label: "Stated maximum a day", value: formatDailyMaximum(medicine.maxPerDay, medicine.unit) });
  }

  if (medicine.courseMode === "cycle") {
    const position = cyclePositionLabel(medicine, date);
    // Absent once the last cycle has passed, which `medicineEndDate` reports below instead.
    if (position) facts.push({ label: "Cycle", value: position });
  }

  if (medicine.courseStart) facts.push({ label: medicine.courseMode === "cycle" ? "Day 1 of cycle 1" : "Started", value: day(medicine.courseStart) });
  const end = medicineEndDate(medicine);
  if (end) facts.push({ label: date > end ? "Course ended" : "Runs until", value: day(end) });
  else if (medicine.courseMode === "ongoing") facts.push({ label: "Course", value: "Ongoing — no end date entered" });

  if (medicine.doseMode === "complex" && medicine.doseStages.length > 1) {
    const stage = doseStageForDate(medicine, date);
    if (stage) {
      const period = stagePeriod(stage);
      facts.push({ label: "Current stage", value: `${stageLabelText(stage.label)}${period ? ` · ${period}` : ""}` });
    }
  }

  if (medicine.requiresInjectionSite) facts.push({ label: "When logging", value: "TED asks which site was used" });

  return facts;
}

/** A stage the user never named still needs something to call it on screen. */
const stageLabelText = (label: string) => label.trim() || "Unnamed stage";

export function doseStageForDate(medicine: Medicine, date = todayKey()) {
  return [...medicine.doseStages].sort((a, b) => a.startDate.localeCompare(b.startDate)).find((stage) => stage.startDate <= date && (!stage.endDate || stage.endDate >= date));
}

/**
 * The dates a dose stage covers, written as a line to read.
 *
 * An open-ended stage is the normal case, not an exceptional one. A fixed combination is
 * stored as a single stage with no end date — that is what the medicine form saves when
 * "my dose changes over time" is left unticked — and the last stage of a taper has none
 * either. `doseStageForDate` has always handled that; the dose sheet formatted it anyway,
 * and `Intl.DateTimeFormat` throws on an invalid date rather than returning something
 * harmless, which took the whole screen down. See NOTES/bugs.md, v0.56.
 *
 * Returns an empty string rather than a placeholder when there is nothing sensible to say,
 * so the caller can leave the line out entirely.
 */
export function stagePeriod(stage: Pick<DoseStage, "startDate" | "endDate">) {
  const day = (value?: string) => {
    if (!value) return undefined;
    const date = new Date(`${value}T12:00:00`);
    return Number.isFinite(date.getTime()) ? formatDay(date) : undefined;
  };
  const from = day(stage.startDate), to = day(stage.endDate);
  if (from && to) return `${from}\u2013${to}`;
  if (from) return `from ${from}`;
  return to ? `until ${to}` : "";
}

export function stageCounts(medicine: Medicine, date = todayKey()) {
  const stage = doseStageForDate(medicine, date);
  return Object.fromEntries(medicine.components.map((component) => [component.id, stage?.componentCounts.find((item) => item.componentId === component.id)?.count ?? component.defaultCount]));
}

export function componentTotal(medicine: Medicine, counts: Record<string, number>) {
  if (!medicine.components.length || medicine.components.some((component) => !Number.isFinite(Number(component.strength)))) return undefined;
  return medicine.components.reduce((total, component) => total + Number(component.strength) * (counts[component.id] ?? component.defaultCount), 0);
}

export function componentSnapshots(medicine: Medicine, counts: Record<string, number>): ComponentSnapshot[] {
  return medicine.components
    .map((component) => ({ strength: component.strength, strengthUnit: medicine.strengthUnit ?? "", count: counts[component.id] ?? component.defaultCount }))
    .filter((item) => item.count > 0);
}

export function componentBreakdown(medicine: Medicine, event?: DoseEvent, date = todayKey()): ComponentSnapshot[] {
  if (event?.componentBreakdown?.length) return event.componentBreakdown;
  if (!medicine.components.length) return [];
  const eventCounts = new Map(event?.componentCounts?.map((item) => [item.componentId, item.count]) ?? []);
  const planned = stageCounts(medicine, date);
  return medicine.components
    .map((component) => ({ strength: component.strength, strengthUnit: medicine.strengthUnit ?? "", count: eventCounts.get(component.id) ?? planned[component.id] ?? component.defaultCount }))
    .filter((item) => item.count > 0);
}

export function componentSummary(medicine: Medicine, event?: DoseEvent, date = todayKey()) {
  return componentBreakdown(medicine, event, date).map((item) => `${item.count} × ${item.strength} ${item.strengthUnit}`.trim()).join(" + ");
}

/**
 * The dose arithmetic written out in full: "2 × 5 mg + 3 × 1 mg = 13 mg".
 *
 * The complex-dose builder asks people to pick counts of each strength they have, then quietly
 * checks the total against the dose their plan states. Showing only the total left the user to
 * trust arithmetic they could not see. Writing the sum out means the form explains its own
 * model — which is most of what a walkthrough would have had to say.
 *
 * Returns undefined when nothing is selected yet, so callers render nothing rather than "0".
 */
export function doseWorking(parts: { strength: string; count: number }[], strengthUnit: string) {
  const used = parts.filter((part) => part.count > 0 && part.strength.trim() !== "" && Number.isFinite(Number(part.strength)));
  if (!used.length) return undefined;
  // Strengths are decimals entered as text, so the sum needs rounding back to something a
  // person would recognise: 0.1 + 0.2 must read as 0.3, not 0.30000000000000004.
  const total = Math.round(used.reduce((sum, part) => sum + Number(part.strength) * part.count, 0) * 1e6) / 1e6;
  const unit = strengthUnit.trim();
  const sum = used.map((part) => `${part.count} × ${part.strength} ${unit}`.trim()).join(" + ");
  // One tablet needs no working: "1 × 5 mg = 5 mg" is three ways of saying the same thing.
  const single = used.length === 1 && used[0].count === 1;
  return { total, sum, text: single ? `${used[0].strength} ${unit}`.trim() : `${sum} = ${total} ${unit}`.trim() };
}

export function plannedAmount(medicine: Medicine, date = todayKey()) {
  if (medicine.doseMode !== "complex") return { amount: medicine.defaultAmount, unit: medicine.unit };
  const counts = stageCounts(medicine, date);
  const totalUnits = Object.values(counts).reduce((total, count) => total + count, 0);
  return { amount: String(totalUnits), unit: medicine.unit };
}

export function correctionChain(current: DoseEvent, all: DoseEvent[]) {
  const chain = [current];
  const seen = new Set([current.id]);
  let pointer = current.correctsEventId;
  while (pointer) {
    if (seen.has(pointer)) break;
    const previous = all.find((event) => event.id === pointer);
    if (!previous) break;
    chain.push(previous);
    seen.add(previous.id);
    pointer = previous.correctsEventId;
  }
  return chain;
}

/**
 * Whether any dose event names this medicine, including corrected and deleted ones.
 *
 * Every dose event must point at a medicine that still exists, so a medicine with history
 * can never be removed outright — doing so would invalidate the store and silently rewrite
 * the dose record. Those are archived instead.
 */
const SPOKEN_NUMBERS: Record<string, number> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20,
};

const normaliseSpeech = (value: string) => value.toLowerCase().replace(/[^a-z0-9.]+/g, " ").replace(/\s+/g, " ").trim();
const spokenNumber = (token: string) => /^\d+(\.\d+)?$/.test(token) ? Number(token) : SPOKEN_NUMBERS[token];

export type DoseSpeechReading = {
  /** Exactly what was heard, always shown so the user can see what TED worked from. */
  transcript: string;
  amount?: string;
  medicine?: Medicine;
  /** Every medicine the words could refer to. More than one means TED must not choose. */
  candidates: Medicine[];
};

/**
 * Read a spoken phrase against the user's own medicines.
 *
 * This never decides anything on its own: it fills the dose form, which the user still
 * confirms, and every existing safeguard — the review step, the daily-limit interrupt —
 * applies unchanged. It only ever matches medicines already entered by the user, and
 * where the words fit more than one it selects none and leaves the choice to them.
 */
export function interpretDoseSpeech(transcript: string, medicines: Medicine[]): DoseSpeechReading {
  const spoken = normaliseSpeech(transcript);
  const available = medicines.filter((medicine) => !medicine.archivedAt);
  const named = available.map((medicine) => ({ medicine, name: normaliseSpeech(medicine.name) })).filter((entry) => entry.name.length > 0);

  // A full name heard in the phrase is the strong signal. Where several fit — "Paracetamol"
  // inside "Paracetamol with codeine" — the longest is the one actually said.
  const full = named.filter((entry) => spoken.includes(entry.name)).sort((a, b) => b.name.length - a.name.length);
  let medicine = full[0]?.medicine;
  let candidates = full.map((entry) => entry.medicine);

  if (!medicine) {
    // Otherwise fall back to a distinctive word. Two possibilities means no choice is made.
    const heard = new Set(spoken.split(" ").filter((word) => word.length >= 4));
    const partial = named.filter((entry) => entry.name.split(" ").some((word) => word.length >= 4 && heard.has(word)));
    candidates = partial.map((entry) => entry.medicine);
    if (partial.length === 1) medicine = partial[0].medicine;
  }

  // Take the medicine's own name out before reading a number, so a strength inside the
  // name — "Paracetamol 500" — is never mistaken for how many were taken.
  const remainder = medicine ? spoken.replace(normaliseSpeech(medicine.name), " ") : spoken;
  let amount: number | undefined;
  const andAHalf = remainder.match(/(\d+(?:\.\d+)?|[a-z]+)\s+and\s+a\s+half/);
  if (andAHalf) {
    const base = spokenNumber(andAHalf[1]);
    if (base !== undefined) amount = base + 0.5;
  }
  if (amount === undefined) {
    for (const token of remainder.split(" ")) {
      const value = spokenNumber(token);
      if (value !== undefined) { amount = value; break; }
    }
  }
  if (amount === undefined && /\bhalf\b/.test(remainder)) amount = 0.5;

  return { transcript: transcript.trim(), amount: amount === undefined ? undefined : String(amount), medicine, candidates };
}

/**
 * Today's scheduled times for one medicine that have no dose recorded against them.
 *
 * A dose logged without picking a card belongs to one of these or to none of them, and only
 * the user knows which. TED offers the list and asks rather than choosing.
 */
export function openScheduledOccurrences(store: TedStore, medicineId: string, now: Date) {
  return buildTodayItems(store, now).filter((item) => item.kind !== "as-needed" && item.state !== "taken" && item.medicine.id === medicineId);
}

export type DoseDay = {
  /** The local calendar day, as `todayKey` writes it. */
  key: string;
  /** "Today · Wed 2 Sep", "Yesterday · Tue 1 Sep", or "Wed 26 Aug". */
  label: string;
  /** In the order they were given, which History supplies newest first. */
  events: DoseEvent[];
  /** Doses actually recorded that day. Deleted entries are shown but never counted. */
  recorded: number;
};

/**
 * History, cut into days.
 *
 * Grouping is by the **local** day a dose was taken, not the recorded day and not UTC: the
 * heading has to match the day the person remembers, and a dose taken at 23:40 belongs to that
 * evening wherever the server thinks it is.
 *
 * The count beside a heading states what was recorded and nothing else. It is never compared
 * with what was scheduled — no "4 of 6", no percentage — because TED does not tell anyone they
 * missed a dose, and a number that invites the comparison is the same thing said quietly.
 * A deleted entry still appears in its day, carrying its own label, but is not counted: it is
 * not a dose that was taken.
 */
export function groupDosesByDay(events: DoseEvent[], voids: DoseVoid[], now = new Date()): DoseDay[] {
  const deleted = new Set(voids.map((entry) => entry.eventId));
  const today = todayKey(now);
  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = todayKey(yesterdayDate);

  const days = new Map<string, DoseEvent[]>();
  for (const event of events) {
    const taken = new Date(event.actualTakenAt);
    // A store can hold anything. An unreadable time gets its own group rather than throwing,
    // and rather than being silently dropped from a record the user is entitled to see.
    const key = Number.isFinite(taken.getTime()) ? todayKey(taken) : "";
    const bucket = days.get(key);
    if (bucket) bucket.push(event); else days.set(key, [event]);
  }

  return [...days.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, dayEvents]) => ({
      key,
      label: !key ? "Date not recorded"
        : key === today ? `Today \u00b7 ${formatDay(new Date(`${key}T12:00:00`))}`
        : key === yesterday ? `Yesterday \u00b7 ${formatDay(new Date(`${key}T12:00:00`))}`
        : formatDay(new Date(`${key}T12:00:00`)),
      events: dayEvents,
      recorded: dayEvents.filter((event) => !deleted.has(event.id)).length,
    }));
}

export type HistoryWeek = {
  /** Monday, as `todayKey` writes it. */
  start: string;
  /** "1–7 September" or "30 August – 5 September", as a reader would say it. */
  label: string;
  /** Always seven, Monday first, including the days with nothing on them. */
  days: DoseDay[];
  /** True when this week contains today, so the view can say so rather than compute it twice. */
  current: boolean;
};

const mondayOf = (date: Date) => {
  const monday = new Date(date);
  monday.setHours(12, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
};

/**
 * One week of history, Monday to Sunday, with every day present.
 *
 * The day view lists only the days that have something on them; a week has to show all seven
 * or it is not a week. That makes the empty ones visible, which is the thing to be careful
 * about — so a day that has not happened yet carries no wording at all rather than reading as
 * a day with nothing done in it. An empty day in the past is described, never marked: TED
 * cannot tell a missed dose from a rest day, a day the medicine was not due, or a day before
 * the medicine existed, and it does not guess.
 */
export function historyWeek(events: DoseEvent[], voids: DoseVoid[], anchor = new Date(), now = new Date()): HistoryWeek {
  const monday = mondayOf(anchor);
  const grouped = new Map(groupDosesByDay(events, voids, now).map((day) => [day.key, day]));
  const today = todayKey(now);

  const days: DoseDay[] = [];
  for (let index = 0; index < 7; index++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    const key = todayKey(date);
    days.push(grouped.get(key) ?? {
      key,
      label: key === today ? `Today \u00b7 ${formatDay(date)}` : formatDay(date),
      events: [],
      recorded: 0,
    });
  }

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const month = (date: Date) => new Intl.DateTimeFormat("en-GB", { month: "long" }).format(date);
  const label = sameMonth
    ? `${monday.getDate()}\u2013${sunday.getDate()} ${month(sunday)}`
    : `${monday.getDate()} ${month(monday)} \u2013 ${sunday.getDate()} ${month(sunday)}`;

  return { start: todayKey(monday), label, days, current: days.some((day) => day.key === today) };
}

export type HistoryMonth = {
  /** The first of the month, as `todayKey` writes it. */
  start: string;
  /** "September 2026". */
  label: string;
  /**
   * Weeks of seven, Monday first — five or six of them, only as many as the month needs.
   *
   * A fixed six would keep the grid from changing height between months, but most months want
   * five, and a permanently empty row costs about 50px of a screen whose real estate is the
   * whole constraint on this view. Paging a month is a deliberate tap, so the shift lands
   * where the reader is already looking.
   *
   * Days outside the month are `undefined` rather than borrowed from the neighbouring one: a
   * cell belonging to another month would be tappable and would then open a day the reader
   * did not ask for.
   */
  weeks: (DoseDay | undefined)[][];
  /** Days in this month that have at least one dose recorded. Never a total to reach. */
  daysLogged: number;
  /** True when this month contains today. */
  current: boolean;
};

/**
 * One month of history as a grid.
 *
 * The whole design question here was bias, and Ed settled it: what makes a medication calendar
 * a scorecard is marking the days that are **empty**, and nothing forces that. So a day with
 * nothing recorded is plain paper — no colour, no outline, no mark — and nothing anywhere is
 * compared with what was scheduled. `daysLogged` says how many days have something on them and
 * is never given a denominator.
 *
 * A month can say *that* something was logged. It cannot say *what*: at 390px a cell is about
 * 47px wide, which is a date and up to three dots and no room for a medicine name. Reading a
 * day is the day list's job, which is why the view pairs with one.
 */
export function historyMonth(events: DoseEvent[], voids: DoseVoid[], anchor = new Date(), now = new Date()): HistoryMonth {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1, 12);
  const grouped = new Map(groupDosesByDay(events, voids, now).map((day) => [day.key, day]));
  const today = todayKey(now);
  const lead = (first.getDay() + 6) % 7;
  const length = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();

  const rows = Math.ceil((lead + length) / 7);
  const weeks: (DoseDay | undefined)[][] = [];
  for (let week = 0; week < rows; week++) {
    const row: (DoseDay | undefined)[] = [];
    for (let column = 0; column < 7; column++) {
      const dayOfMonth = week * 7 + column - lead + 1;
      if (dayOfMonth < 1 || dayOfMonth > length) { row.push(undefined); continue; }
      const date = new Date(first.getFullYear(), first.getMonth(), dayOfMonth, 12);
      const key = todayKey(date);
      row.push(grouped.get(key) ?? {
        key,
        label: key === today ? `Today \u00b7 ${formatDay(date)}` : formatDay(date),
        events: [],
        recorded: 0,
      });
    }
    weeks.push(row);
  }

  const days = weeks.flat().filter(Boolean) as DoseDay[];
  return {
    start: todayKey(first),
    label: `${new Intl.DateTimeFormat("en-GB", { month: "long" }).format(first)} ${first.getFullYear()}`,
    weeks,
    daysLogged: days.filter((day) => day.recorded > 0).length,
    current: days.some((day) => day.key === today),
  };
}

export function medicineHasHistory(store: TedStore, medicineId: string) {
  return store.doseEvents.some((event) => event.medicineId === medicineId);
}

/* --------------------------------------------------------------------------------------
 * Looking after someone else's copy of TED.
 *
 * A new user has nothing to lose on their first day and everything to lose by their third
 * week, and the two things that actually lose it — never taking a backup, and leaving TED
 * as a browser tab where iOS eventually clears it — both happen quietly.
 * ----------------------------------------------------------------------------------- */

/** How long a backup may lag behind before it is worth mentioning again. */
const BACKUP_GRACE_DAYS = 14;

export type BackupReminder = "none" | "never" | "stale";

/**
 * Whether there is enough in this copy of TED to be worth keeping, and no recent copy of it.
 * Silent until there is something to lose, and silent again for a fortnight after a backup,
 * so it never becomes noise that gets tuned out.
 */
export function backupReminder(store: TedStore, now = new Date()): BackupReminder {
  if (store.medicines.length === 0 && store.doseEvents.length === 0) return "none";
  if (!store.lastBackupAt) return "never";
  if (store.updatedAt <= store.lastBackupAt) return "none";
  const daysSince = (now.getTime() - new Date(store.lastBackupAt).getTime()) / 86_400_000;
  return Number.isFinite(daysSince) && daysSince >= BACKUP_GRACE_DAYS ? "stale" : "none";
}

export type SupportFacts = {
  version: string;
  /** Running from the Home Screen rather than as a browser tab. */
  installed: boolean;
  storage: string;
  online: boolean;
};

/**
 * A short summary someone can screenshot when something goes wrong.
 *
 * Deliberately counts rather than names: this is meant to be sent to another person, so it
 * must never carry a medicine name, a dose, a note or a time. `tests/support.test.ts`
 * asserts that against a store whose every field is a recognisable word.
 */
export function supportSummary(store: TedStore, facts: SupportFacts, now = new Date()): string[] {
  const active = store.medicines.filter((medicine) => !medicine.archivedAt).length;
  return [
    `TED ${facts.version.replace(/^TED\s*/i, "")}`,
    `Installed to Home Screen: ${facts.installed ? "yes" : "no"}`,
    `Storage: ${facts.storage}`,
    `Connection: ${facts.online ? "online" : "offline"}`,
    `Medicines: ${active} active, ${store.medicines.length - active} no longer taking`,
    `Dose entries: ${store.doseEvents.length}, deleted ${store.doseVoids.length}`,
    `Last backup: ${store.lastBackupAt ? formatDay(store.lastBackupAt) : "none recorded"}`,
    `Data first created: ${formatDay(store.createdAt)}`,
    `Checked at: ${formatDay(now)} ${formatClock(now)}`,
  ];
}

/* --------------------------------------------------------------------------------------
 * Ordering the medicines list.
 *
 * Sorting only rearranges what is already on screen: no medicine is ever filtered out, and
 * anything without the detail being sorted on falls into "Other" rather than disappearing.
 * ----------------------------------------------------------------------------------- */

export type MedicineSort = "effect" | "type" | "added" | "name" | "refill";
export type MedicineGroup = { key: string; label: string; medicines: Medicine[] };

export const MEDICINE_SORTS: { value: MedicineSort; label: string }[] = [
  { value: "name", label: "Name" },
  // The value is what a remembered preference holds, so it stays "effect" — renaming it
  // would silently reset every reader's saved sort. Only the word on screen changes: "Effect"
  // reads as a side effect, which this is not.
  { value: "effect", label: "Purpose" },
  { value: "type", label: "Type" },
  { value: "added", label: "Date added" },
  { value: "refill", label: "Time to refill" },
];

/** Medicines not counting a prescription, which is most of them and is not a fault. */
const UNCOUNTED_GROUP = "Not counting a prescription";

const OTHER_GROUP = "Other";
const byName = (a: Medicine, b: Medicine) => a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" });

/**
 * Arrange medicines for display. Always returns every medicine passed in — `effect` and
 * `type` return one group per heading, `added` and `name` a single unheaded group.
 */
/**
 * Soonest to run out first, with everything else named and kept.
 *
 * Most medicines are not counting a prescription, and that is not a fault or an omission to be
 * nagged about — so they keep a group of their own rather than being hidden or given an
 * invented date that would sort them among the real ones. A sort that loses medicines is worse
 * than no sort: this one arranges, it never filters.
 *
 * A medicine that is counting but whose schedule gives no usable rate has a real remaining
 * count and no days figure. It sorts after everything with a date, because "soonest" cannot be
 * claimed of it, and before the uncounted, because it is still counting something.
 */
function byRefill(medicines: Medicine[], events: DoseEvent[], voids: DoseVoid[]): MedicineGroup[] {
  const counted: { medicine: Medicine; daysLeft?: number }[] = [];
  const uncounted: Medicine[] = [];

  for (const medicine of medicines) {
    const state = supplyState(medicine, events, voids);
    if (state) counted.push({ medicine, daysLeft: state.daysLeft });
    else uncounted.push(medicine);
  }

  counted.sort((a, b) => {
    if (a.daysLeft === undefined && b.daysLeft === undefined) return byName(a.medicine, b.medicine);
    if (a.daysLeft === undefined) return 1;
    if (b.daysLeft === undefined) return -1;
    return a.daysLeft - b.daysLeft || byName(a.medicine, b.medicine);
  });

  const groups: MedicineGroup[] = [];
  if (counted.length) groups.push({ key: "counting", label: "Counting down", medicines: counted.map((entry) => entry.medicine) });
  if (uncounted.length) groups.push({ key: "uncounted", label: UNCOUNTED_GROUP, medicines: [...uncounted].sort(byName) });
  return groups;
}

export function groupMedicines(medicines: Medicine[], sort: MedicineSort, events: DoseEvent[] = [], voids: DoseVoid[] = []): MedicineGroup[] {
  if (sort === "name") return [{ key: "all", label: "", medicines: [...medicines].sort(byName) }];
  if (sort === "refill") return byRefill(medicines, events, voids);
  if (sort === "added") {
    // Newest first, so something just added is at the top where it is being looked for.
    // Equal timestamps fall back to the name rather than to whatever order they arrived in.
    const added = [...medicines].sort((a, b) => b.createdAt.localeCompare(a.createdAt) || byName(a, b));
    return [{ key: "all", label: "", medicines: added }];
  }

  const heading = (medicine: Medicine) => {
    // One row per medicine, so a medicine taken for several things is filed under the first
    // of them rather than repeated under each. Sorting rearranges; it never duplicates.
    const value = sort === "effect" ? medicine.purposes?.[0]?.trim() : formLabel(medicine).trim();
    return value ? value : OTHER_GROUP;
  };

  const groups = new Map<string, Medicine[]>();
  for (const medicine of medicines) {
    const label = heading(medicine);
    const existing = groups.get(label);
    if (existing) existing.push(medicine); else groups.set(label, [medicine]);
  }

  return [...groups.entries()]
    .map(([label, items]) => ({ key: label, label, medicines: items.sort(byName) }))
    // Alphabetical by heading, with "Other" last — it is the absence of an answer rather
    // than one of them, so it does not belong among the real ones.
    .sort((a, b) => (a.label === OTHER_GROUP ? 1 : 0) - (b.label === OTHER_GROUP ? 1 : 0)
      || a.label.localeCompare(b.label, "en-GB", { sensitivity: "base" }));
}

export function latestMedicineCheck(store: TedStore, medicineId: string) {
  return store.medicineChecks
    .filter((check) => check.medicineId === medicineId)
    .sort((a, b) => b.checkedAt.localeCompare(a.checkedAt))[0];
}

export function medicineCheckIsCurrent(store: TedStore, medicine: Medicine) {
  return latestMedicineCheck(store, medicine.id)?.medicineUpdatedAt === medicine.updatedAt;
}

type LegacyMedicine = { id:string; name:string; route:"tablet"|"liquid"|"injection"|"other"; defaultAmount:string; unit:string; scheduleTimes:string[]; courseStart?:string; courseEnd?:string; requiresInjectionSite:boolean; createdAt:string; updatedAt:string };
type LegacyDose = Omit<DoseEvent, "correctionFields" | "correctionOther"> & { correctionReason?: string };
type LegacyV3Form = MedicineForm | "pill-capsule" | "cream-gel" | "sachet-powder";
type V8Medicine = Omit<Medicine, "maxPerDay"> & { maxAmountPerDay?: number };
type V9Medicine = Omit<V10Medicine, "cycleLengthDays" | "cycleDays" | "cycleCount" | "courseMode"> & { courseMode: "ongoing" | "duration" | "end-date" };
type V10Medicine = Omit<Medicine, "purposes"> & { purpose?: string };
type V10Store = Omit<TedStore, "schemaVersion" | "medicines"> & { schemaVersion: 10; medicines: V10Medicine[] };
type V9Store = Omit<V10Store, "schemaVersion" | "medicines"> & { schemaVersion: 9; medicines: V9Medicine[] };
type V8Store = Omit<V9Store, "schemaVersion" | "medicines"> & { schemaVersion: 8; medicines: V8Medicine[] };
type V7Store = Omit<V8Store, "schemaVersion"> & { schemaVersion: 7 };
type V6Medicine = Omit<V8Medicine, "archivedAt">;
type V6Store = Omit<V8Store, "schemaVersion" | "medicines"> & { schemaVersion: 6; medicines: V6Medicine[] };
type V5Store = Omit<V6Store, "schemaVersion" | "medicineChecks" | "onboardingCompletedAt" | "lastBackupAt" | "lastSeenAppVersion"> & { schemaVersion: 5 };
type V4Medicine = Omit<V8Medicine, "maxAmountPerDay"> & { maxDosesPerDay?: number };
type V4Store = Omit<V5Store, "schemaVersion" | "medicines" | "doseVoids"> & { schemaVersion: 4; medicines: V4Medicine[] };
type V3Medicine = Omit<V4Medicine, "form"> & { form: LegacyV3Form };
type V3Store = Omit<V4Store, "schemaVersion" | "medicines"> & { schemaVersion: 3; medicines: V3Medicine[] };
type V2Medicine = Omit<V3Medicine, "doseMode" | "strength" | "strengthUnit" | "doseStages" | "intervalHours" | "intervalAnchorDate" | "intervalAnchorTime" | "asNeededIntervalHours"> & { scheduleMode: "fixed" | "as-needed" };
type V2Store = Omit<V3Store, "schemaVersion" | "medicines"> & { schemaVersion: 2; medicines: V2Medicine[] };

function normalizeForm(form: LegacyV3Form, customForm?: string): Pick<Medicine, "form" | "customForm"> {
  if (form === "pill-capsule") return { form: "capsule" };
  if (form === "cream-gel") return { form: "cream-ointment" };
  if (form === "sachet-powder") return { form: "other", customForm: customForm || "Sachet / powder" };
  return { form, customForm };
}

/**
 * v8 → v9 replaces the bare `maxAmountPerDay` number with `maxPerDay`, which carries the
 * basis the user was counting on.
 *
 * Every stored value arrives on a **units** basis, because that is what every version from v5
 * to v0.32 compared it against — the total amount recorded that day. Recording that basis is
 * therefore a statement of what TED has been doing, not a reinterpretation: no number changes
 * and no medicine starts warning differently. Where one dose is more than one unit the user
 * may have meant doses, and `dailyLimitNeedsConfirming` keeps surfacing exactly those for the
 * user to re-confirm. TED does not convert them.
 */
/**
 * v9 → v10 adds the cycle fields. Nothing is converted: no v9 medicine can carry a cycle, so
 * every one of them is already a valid v10 medicine with all three absent, and absent means
 * "no cycle" everywhere it is read. The step exists so the version states which shape a backup
 * may contain — the same reason `migrateV7` exists. See NOTES/bugs.md on v0.30.
 */
function migrateV9(raw: V9Store): V10Store {
  return { ...raw, schemaVersion: 10, medicines: raw.medicines as V10Medicine[] };
}

/**
 * v10 → v11 turns the single `purpose` string into the `purposes` list, because a medicine
 * can be taken for more than one thing and TED could only record one.
 *
 * Nothing is interpreted. A stored purpose becomes the one entry of the new list, whatever it
 * says: the old field allowed free text, so plenty of them will not match a v11 preset, and
 * one that does not is kept exactly as typed rather than dropped or mapped onto the preset it
 * looks nearest to. Guessing that "Nausea relief" means "Nausea / sickness" would be TED
 * rewriting the user's own words for them.
 *
 * A blank or whitespace-only purpose becomes no list at all, which is what every reader
 * already treated it as.
 */
type V11Medicine = Omit<Medicine, "supply">;
type V11Store = Omit<TedStore, "schemaVersion" | "medicines"> & { schemaVersion: 11; medicines: V11Medicine[] };

/**
 * v11 → v12 adds `Medicine.supply`, and adds nothing to any existing medicine.
 *
 * Supply tracking is opt-in per medicine and absent is its normal state, so there is nothing to
 * convert and nothing to invent: a medicine that was not counting its packet before this
 * release is not counting it after. The version number moves so a backup declares the shape it
 * may now contain, and so a v11 backup is still accepted.
 *
 * Idempotent by construction — it reads no field it writes.
 */
function migrateV11(raw: V11Store): TedStore {
  return { ...raw, schemaVersion: 12 };
}

function migrateV10(raw: V10Store): V11Store {
  const medicines = raw.medicines.map(({ purpose, ...medicine }): Medicine => {
    const kept = purpose?.trim();
    return kept ? { ...medicine, purposes: [kept] } : medicine;
  });
  return { ...raw, schemaVersion: 11, medicines } as unknown as V11Store;
}

function migrateV8(raw: V8Store): V9Store {
  const medicines = raw.medicines.map(({ maxAmountPerDay, ...medicine }): Medicine =>
    maxAmountPerDay === undefined ? medicine : { ...medicine, maxPerDay: { value: maxAmountPerDay, basis: "units", basisAssumed: true } });
  return { ...raw, schemaVersion: 9, medicines } as V9Store;
}

/**
 * v7 → v8 widens `DoseStage.endDate` to optional. Every stage written by v7 has one, so there
 * is nothing to convert; this exists so the version number tells the truth about the shape a
 * backup may now contain, and so a v7 backup is still accepted.
 */
function migrateV7(raw: V7Store): V8Store {
  return { ...raw, schemaVersion: 8 };
}

function migrateV6(raw: V6Store): V7Store {
  // `archivedAt` is optional and absent on every earlier record, so nothing is rewritten.
  return { ...raw, schemaVersion: 7 };
}

function migrateV5(raw: V5Store): V6Store {
  return {
    ...raw,
    schemaVersion: 6,
    medicineChecks: [],
    onboardingCompletedAt: raw.createdAt,
    lastSeenAppVersion: "TED v0.8",
  };
}

function migrateV4(raw: V4Store): V5Store {
  const medicines = raw.medicines.map((medicine): V8Medicine => {
    const { maxDosesPerDay, ...current } = medicine;
    return { ...current, maxAmountPerDay: maxDosesPerDay };
  });
  return { ...raw, schemaVersion: 5, medicines, doseVoids: [], updatedAt: new Date().toISOString() };
}

function migrateV3(raw: V3Store): V4Store {
  const medicines = raw.medicines.map((medicine): V4Medicine => ({ ...medicine, ...normalizeForm(medicine.form, medicine.customForm) }));
  return { ...raw, schemaVersion: 4, medicines, updatedAt: new Date().toISOString() };
}

function migrateV2(raw: V2Store): V4Store {
  const stageIds = new Map<string, string>();
  const medicines = raw.medicines.map((medicine): V4Medicine => {
    const complex = medicine.components.length > 0;
    const stageId = uid();
    stageIds.set(medicine.id, stageId);
    const legacyEnd = medicine.courseMode === "end-date" ? medicine.courseEnd : medicine.courseMode === "duration" && medicine.courseStart && medicine.courseDurationValue ? courseDurationEnd(medicine.courseStart, medicine.courseDurationValue, medicine.courseDurationUnit ?? "days") : undefined;
    const totalUnits = medicine.components.reduce((total, component) => total + component.defaultCount, 0);
    const normalized = normalizeForm(medicine.form, medicine.customForm);
    return {
      ...medicine,
      ...normalized,
      defaultAmount: complex ? String(totalUnits) : medicine.defaultAmount,
      unit: complex ? defaultUnitForForm(normalized.form) : medicine.unit,
      doseMode: complex ? "complex" : "simple",
      strengthUnit: complex ? medicine.unit : undefined,
      doseStages: complex ? [{ id: stageId, label: "Current period", startDate: medicine.courseStart ?? "1900-01-01", endDate: legacyEnd ?? "2999-12-31", targetAmount: medicine.defaultAmount, componentCounts: medicine.components.map((component) => ({ componentId: component.id, count: component.defaultCount })) }] : [],
    };
  });
  const oldMedicineMap = new Map(raw.medicines.map((medicine) => [medicine.id, medicine]));
  const newMedicineMap = new Map(medicines.map((medicine) => [medicine.id, medicine]));
  const doseEvents = raw.doseEvents.map((event): DoseEvent => {
    const oldMedicine = oldMedicineMap.get(event.medicineId);
    const newMedicine = newMedicineMap.get(event.medicineId);
    if (!oldMedicine?.components.length || !newMedicine) return event;
    const counts = Object.fromEntries(oldMedicine.components.map((component) => [component.id, event.componentCounts?.find((item) => item.componentId === component.id)?.count ?? component.defaultCount]));
    return {
      ...event,
      amount: String(Object.values(counts).reduce((total, count) => total + Number(count), 0)), unit: newMedicine.unit,
      doseStageId: stageIds.get(event.medicineId), targetAmount: event.amount, targetUnit: event.unit,
      componentBreakdown: componentSnapshots(newMedicine, counts),
    };
  });
  return { ...raw, schemaVersion: 4, medicines, doseEvents, updatedAt: new Date().toISOString() };
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const nonEmptyString = (value: unknown) => typeof value === "string" && value.trim().length > 0;
const validIsoDate = (value: unknown) => typeof value === "string" && Number.isFinite(new Date(value).getTime());

const wholeNumberAtLeast = (value: unknown, least: number) => typeof value === "number" && Number.isInteger(value) && value >= least;

/**
 * A cycle is three numbers that have to agree, and each way they can disagree stores something
 * TED would then have to guess about: a cycle with no taking days at all, a day numbered beyond
 * the end of its own cycle, or a count of zero cycles. Refused here rather than rendered.
 *
 * Off a cycle, all three must be absent — carrying a stale cycle on a medicine switched back to
 * a plain course would make it reappear if the user ever switched again.
 */
/**
 * A supply is four values that have to agree, and a wrong one produces a confident wrong date
 * to reorder by — so a backup carrying nonsense is rejected rather than carried into the app.
 *
 * `quantity` may legitimately be zero: a packet counted down to nothing is a real state, and
 * the one most likely to be sitting in a backup at the moment it matters.
 */
function validSupply(value: unknown) {
  if (value === undefined) return true;
  if (!isRecord(value)) return false;
  if (typeof value.quantity !== "number" || !Number.isFinite(value.quantity) || value.quantity < 0) return false;
  if (!wholeNumberAtLeast(value.warnDays, 1)) return false;
  if (!nonEmptyString(value.from) || !/^\d{4}-\d{2}-\d{2}$/.test(value.from as string)) return false;
  return validIsoDate(value.recordedAt);
}

function validCycle(value: Record<string, unknown>) {
  if (value.courseMode !== "cycle") {
    return value.cycleLengthDays === undefined && value.cycleDays === undefined && value.cycleCount === undefined;
  }
  if (!nonEmptyString(value.courseStart)) return false;
  if (!wholeNumberAtLeast(value.cycleLengthDays, 1)) return false;
  if (value.cycleCount !== undefined && !wholeNumberAtLeast(value.cycleCount, 1)) return false;
  if (!Array.isArray(value.cycleDays) || value.cycleDays.length === 0) return false;
  const seen = new Set<number>();
  for (const day of value.cycleDays) {
    if (!wholeNumberAtLeast(day, 1) || (day as number) > (value.cycleLengthDays as number) || seen.has(day as number)) return false;
    seen.add(day as number);
  }
  return true;
}

export function validateTedStore(input: unknown): TedStore {
  if (!isRecord(input) || input.schemaVersion !== 12 || !isRecord(input.profile) || !nonEmptyString(input.profile.id) || !nonEmptyString(input.profile.displayName) || !Array.isArray(input.medicines) || !Array.isArray(input.doseEvents) || !Array.isArray(input.doseVoids) || !Array.isArray(input.medicineChecks)) throw new Error("Invalid TED backup");
  if ((input.onboardingCompletedAt !== undefined && !validIsoDate(input.onboardingCompletedAt)) || (input.lastBackupAt !== undefined && !validIsoDate(input.lastBackupAt)) || (input.lastSeenAppVersion !== undefined && !nonEmptyString(input.lastSeenAppVersion)) || !validIsoDate(input.createdAt) || !validIsoDate(input.updatedAt)) throw new Error("Invalid TED app data");
  const medicineIds = new Set<string>();
  for (const value of input.medicines) {
    if (!isRecord(value) || !nonEmptyString(value.id) || medicineIds.has(value.id as string) || !nonEmptyString(value.name) || !nonEmptyString(value.unit) || !nonEmptyString(value.defaultAmount) || !nonEmptyString(value.form) || !Object.hasOwn(FORM_LABELS, String(value.form)) || !["simple","complex"].includes(String(value.doseMode)) || !["fixed","interval","as-needed"].includes(String(value.scheduleMode)) || !Array.isArray(value.scheduleTimes) || !Array.isArray(value.components) || !Array.isArray(value.doseStages) || typeof value.requiresInjectionSite !== "boolean" || (value.archivedAt !== undefined && !validIsoDate(value.archivedAt)) || (value.maxPerDay !== undefined && (!isRecord(value.maxPerDay) || typeof value.maxPerDay.value !== "number" || !Number.isFinite(value.maxPerDay.value) || value.maxPerDay.value <= 0 || !["doses","units"].includes(String(value.maxPerDay.basis))))) throw new Error("Invalid TED medicine data");
    if (!validCycle(value)) throw new Error("Invalid TED cycle data");
    if (value.purposes !== undefined && (!Array.isArray(value.purposes) || !value.purposes.every(nonEmptyString))) throw new Error("Invalid TED medicine data");
    if (!validSupply(value.supply)) throw new Error("Invalid TED supply data");
    medicineIds.add(value.id as string);
  }
  const eventIds = new Set<string>();
  for (const value of input.doseEvents) {
    if (!isRecord(value) || !nonEmptyString(value.id) || eventIds.has(value.id as string) || !nonEmptyString(value.medicineId) || !medicineIds.has(value.medicineId as string) || !nonEmptyString(value.actorId) || !validIsoDate(value.actualTakenAt) || !nonEmptyString(value.amount) || !nonEmptyString(value.unit) || !validIsoDate(value.recordedAt)) throw new Error("Invalid TED dose history");
    eventIds.add(value.id as string);
  }
  const correctionTargets = new Set<string>();
  const corrections = new Map<string, string>();
  for (const value of input.doseEvents) {
    if (!isRecord(value) || value.correctsEventId === undefined) continue;
    if (!nonEmptyString(value.correctsEventId) || !eventIds.has(value.correctsEventId as string) || value.correctsEventId === value.id || correctionTargets.has(value.correctsEventId as string)) throw new Error("Invalid TED correction history");
    correctionTargets.add(value.correctsEventId as string);
    corrections.set(value.id as string, value.correctsEventId as string);
  }
  for (const eventId of corrections.keys()) {
    const seen = new Set([eventId]);
    let pointer = corrections.get(eventId);
    while (pointer) {
      if (seen.has(pointer)) throw new Error("Invalid TED correction history");
      seen.add(pointer);
      pointer = corrections.get(pointer);
    }
  }
  const voidIds = new Set<string>(), voidedEvents = new Set<string>();
  for (const value of input.doseVoids) {
    if (!isRecord(value) || !nonEmptyString(value.id) || voidIds.has(value.id as string) || !nonEmptyString(value.eventId) || !eventIds.has(value.eventId as string) || voidedEvents.has(value.eventId as string) || !nonEmptyString(value.actorId) || !validIsoDate(value.recordedAt)) throw new Error("Invalid TED deletion history");
    voidIds.add(value.id as string);
    voidedEvents.add(value.eventId as string);
  }
  const checkIds = new Set<string>();
  for (const value of input.medicineChecks) {
    if (!isRecord(value) || !nonEmptyString(value.id) || checkIds.has(value.id as string) || !nonEmptyString(value.medicineId) || !medicineIds.has(value.medicineId as string) || !nonEmptyString(value.actorId) || !validIsoDate(value.checkedAt) || !validIsoDate(value.medicineUpdatedAt)) throw new Error("Invalid TED medicine check history");
    checkIds.add(value.id as string);
  }
  return input as TedStore;
}

export function migrateStore(input: unknown): TedStore {
  const raw = input as { schemaVersion?: number; profile?: TedStore["profile"]; medicines?: unknown[]; doseEvents?: unknown[]; createdAt?: string; updatedAt?: string };
  if (!raw || !Array.isArray(raw.medicines) || !Array.isArray(raw.doseEvents) || !raw.profile?.id) throw new Error("Invalid TED backup");
  if (raw.schemaVersion === 12) return validateTedStore(raw);
  if (raw.schemaVersion === 11) return validateTedStore(migrateV11(raw as unknown as V11Store));
  if (raw.schemaVersion === 10) return validateTedStore(migrateV11(migrateV10(raw as V10Store)));
  if (raw.schemaVersion === 9) return validateTedStore(migrateV11(migrateV10(migrateV9(raw as V9Store))));
  if (raw.schemaVersion === 8) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(raw as V8Store)))));
  if (raw.schemaVersion === 7) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(raw as V7Store))))));
  if (raw.schemaVersion === 6) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(migrateV6(raw as V6Store)))))));
  if (raw.schemaVersion === 5) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(migrateV6(migrateV5(raw as V5Store))))))));
  if (raw.schemaVersion === 4) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(migrateV6(migrateV5(migrateV4(raw as V4Store)))))))));
  if (raw.schemaVersion === 3) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(migrateV6(migrateV5(migrateV4(migrateV3(raw as V3Store))))))))));
  if (raw.schemaVersion === 2) return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(migrateV6(migrateV5(migrateV4(migrateV2(raw as V2Store))))))))));
  if (raw.schemaVersion !== 1) throw new Error("Unsupported TED backup");
  const medicines = (raw.medicines as LegacyMedicine[]).map((medicine): V2Medicine => ({
    id: medicine.id, name: medicine.name,
    form: medicine.route === "other" ? "other" : medicine.route,
    defaultAmount: medicine.defaultAmount, unit: medicine.unit, components: [],
    scheduleMode: "fixed", scheduleTimes: medicine.scheduleTimes ?? [], dayPattern: "daily", weekdays: [0,1,2,3,4,5,6],
    courseMode: medicine.courseEnd ? "end-date" : "ongoing", courseStart: medicine.courseStart, courseEnd: medicine.courseEnd,
    requiresInjectionSite: medicine.requiresInjectionSite, createdAt: medicine.createdAt, updatedAt: medicine.updatedAt,
  }));
  const doseEvents = (raw.doseEvents as LegacyDose[]).map((event): DoseEvent => {
    const { correctionReason, ...rest } = event;
    return { ...rest, correctionFields: correctionReason ? ["other"] : undefined, correctionOther: correctionReason };
  });
  return validateTedStore(migrateV11(migrateV10(migrateV9(migrateV8(migrateV7(migrateV6(migrateV5(migrateV4(migrateV2({ schemaVersion: 2, profile: raw.profile, medicines, doseEvents, createdAt: raw.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() }))))))))));
}

const minutesToTime = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

export function makeSampleStore(): TedStore {
  const now = new Date();
  const createdAt = now.toISOString();
  const date = todayKey(now);
  const minute = now.getHours() * 60 + now.getMinutes();
  const takenTime = minutesToTime(Math.max(0, minute - 120));
  const dueTime = minutesToTime(Math.max(0, minute - 30));
  const upcomingTime = minutesToTime(Math.min(1439, minute + 120));
  const stageEnd = addDays(date, 4), nextStageStart = addDays(date, 5), nextStageEnd = addDays(date, 9);
  const complexId = uid(), liquidId = uid(), injectionId = uid(), intervalId = uid(), prnId = uid();
  const componentTen = uid(), componentOne = uid(), stageOne = uid(), stageTwo = uid();
  const scheduledFor = isoFromLocal(date, takenTime);
  const previousDay = new Date(now); previousDay.setDate(previousDay.getDate() - 1); previousDay.setHours(20, 12, 0, 0);
  const prnTaken = new Date(now); prnTaken.setMinutes(Math.max(0, now.getMinutes() - 10), 0, 0);
  const base = { doseMode: "simple" as const, strength: undefined, strengthUnit: undefined, components: [], doseStages: [], dayPattern: "daily" as const, weekdays: [0,1,2,3,4,5,6], courseMode: "ongoing" as const, requiresInjectionSite: false, createdAt, updatedAt: createdAt };
  const complexComponents: DoseComponent[] = [{ id: componentTen, strength: "10", defaultCount: 2 }, { id: componentOne, strength: "1", defaultCount: 3 }];
  const medicines: Medicine[] = [
    { ...base, id: complexId, name: "Sample changing-dose tablets", form: "tablet", defaultAmount: "5", unit: "tablet", doseMode: "complex", strengthUnit: "mg", components: complexComponents, doseStages: [
      { id: stageOne, label: "Period 1", startDate: date, endDate: stageEnd, targetAmount: "23", componentCounts: [{ componentId: componentTen, count: 2 }, { componentId: componentOne, count: 3 }] },
      { id: stageTwo, label: "Period 2", startDate: nextStageStart, endDate: nextStageEnd, targetAmount: "12", componentCounts: [{ componentId: componentTen, count: 1 }, { componentId: componentOne, count: 2 }] },
    ], scheduleMode: "fixed", scheduleTimes: [takenTime] },
    { ...base, id: liquidId, name: "Sample alternate-day liquid", form: "liquid", defaultAmount: "5", unit: "mL", scheduleMode: "fixed", scheduleTimes: [dueTime], dayPattern: "alternate", alternateDayAnchor: date },
    { ...base, id: injectionId, name: "Sample recovery injection", form: "injection", defaultAmount: "1", unit: "injection", strength: "20", strengthUnit: "mg", scheduleMode: "fixed", scheduleTimes: [upcomingTime], requiresInjectionSite: true },
    { ...base, id: intervalId, name: "Sample interval medicine", form: "tablet", defaultAmount: "2", unit: "tablet", scheduleMode: "interval", scheduleTimes: [], intervalHours: 6, intervalAnchorDate: date, intervalAnchorTime: dueTime, purposes: ["Pain relief"] },
    { ...base, id: prnId, name: "Sample as-needed medicine", form: "capsule", defaultAmount: "1", unit: "capsule", scheduleMode: "as-needed", scheduleTimes: [], asNeededIntervalHours: 4, maxPerDay: { value: 4, basis: "doses" }, purposes: ["Nausea / sickness"] },
  ];
  const doseEvents: DoseEvent[] = [
    { id: uid(), medicineId: complexId, actorId: "local-owner", scheduleKey: `${date}|${complexId}|${takenTime}`, scheduledFor, actualTakenAt: new Date(Math.min(now.getTime(), new Date(scheduledFor).getTime() + 6 * 60_000)).toISOString(), amount: "5", unit: "tablet", doseStageId: stageOne, targetAmount: "23", targetUnit: "mg", componentCounts: [{ componentId: componentTen, count: 2 }, { componentId: componentOne, count: 3 }], componentBreakdown: [{ strength: "10", strengthUnit: "mg", count: 2 }, { strength: "1", strengthUnit: "mg", count: 3 }], note: "Synthetic example", recordedAt: createdAt },
    { id: uid(), medicineId: injectionId, actorId: "local-owner", actualTakenAt: previousDay.toISOString(), amount: "1", unit: "injection", injectionSite: { region: "Abdomen", side: "right" }, note: "Synthetic example", recordedAt: createdAt },
    { id: uid(), medicineId: prnId, actorId: "local-owner", actualTakenAt: prnTaken.toISOString(), amount: "1", unit: "capsule", recordedAt: createdAt },
  ];
  return { schemaVersion: 12, profile: { id: "local-owner", displayName: "Me" }, medicines, doseEvents, doseVoids: [], medicineChecks: [], onboardingCompletedAt: createdAt, lastSeenAppVersion: CURRENT_APP_VERSION, createdAt, updatedAt: createdAt };
}

export function makeEmptyStore(): TedStore {
  const createdAt = new Date().toISOString();
  return {
    schemaVersion: 12,
    profile: { id: "local-owner", displayName: "Me" },
    medicines: [],
    doseEvents: [],
    doseVoids: [],
    medicineChecks: [],
    lastSeenAppVersion: CURRENT_APP_VERSION,
    createdAt,
    updatedAt: createdAt,
  };
}
